// Auth-aware fetch wrapper. Handles proactive JWT expiry and 401/403-INVALID_TOKEN
// responses by dispatching a global `sessionExpired` event. Extracted from
// services/auth.js to break the circular import with utils/safeFetch.ts.
//
// Callers should prefer safeAuthFetch from utils/safeFetch, which composes this
// with a throw-on-!ok contract. This module is on the allowlist for direct
// fetch() usage (see frontend/src/__tests__/noUncheckedFetch.test.js).

import { API_BASE_URL as _API_BASE_URL } from '../config/api.js';

const API_BASE = _API_BASE_URL;

// Flag to prevent multiple simultaneous session expiry events.
let sessionExpiredTriggered = false;

export function resetSessionExpiredFlag() {
  sessionExpiredTriggered = false;
}

const AUTH_TOKEN_STORAGE_KEY = 'chatbot_auth_token';

// #3578 L3-R2: the ONE chokepoint for reading and clearing the session token.
// A browser that blocks storage (iOS Safari with "Block All Cookies", some
// in-app webviews, a sandboxed iframe) throws SecurityError on ANY
// localStorage access. Unguarded, that throw escaped AuthContext's restore
// before its try and left the app on the loading spinner forever.
//
// Fail direction (plan check row 1): an unreadable slot is reported as "no
// token". Nothing on this page can read the token either (authFetch would send
// none, getCurrentUser would throw "No authentication token"), so treating it
// as absent is the truthful answer for THIS page. What must NOT follow is a
// guest mint whose token can never be stored: AuthContext.loginAsGuest probes
// writability (getAuthStorageFailure) and stops with STORAGE_UNAVAILABLE first.
export function getStoredAuthToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Returns true when the slot was cleared, false when storage refused (plan
// check row 9: a failed clear is observable to the caller, not silent).
export function clearStoredAuthToken() {
  try {
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

// Throws a coded STORAGE_UNAVAILABLE error when storage refuses the write, so
// every caller (login(), testLogin) fails loudly with one recognisable code
// instead of a raw SecurityError, and none can believe a session was stored.
export function storeAuthToken(token) {
  try {
    localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } catch (cause) {
    const err = new Error('This browser is blocking storage, so a sign-in cannot be kept.');
    err.code = 'STORAGE_UNAVAILABLE';
    err.cause = cause;
    throw err;
  }
}

// #3578 (Gilbert, 2026-09-29): a browser that cannot store a session gets a
// clear stop BEFORE any guest row is minted server-side. A write-and-remove of
// a throwaway key, so the probe itself never touches the session slot.
//
// #3636 item 3: reports WHY the write failed, not just that it did. Returns
// null when writable, 'quota' when storage is full (QuotaExceededError), and
// 'security' for anything else (SecurityError under Block All Cookies, a
// sandboxed iframe, no storage at all). The two need different answers: a
// full store can still hold the guest token that is ALREADY in it, so it must
// not block reuse; a blocked store can hold nothing. `probeBytes` sizes the
// throwaway value, so a caller about to write a NEW token can ask whether
// something token-sized would fit (a 1-byte write can succeed where a JWT
// cannot).
export function getAuthStorageFailure(probeBytes = 1) {
  try {
    const probeKey = '__tacit_storage_probe__';
    localStorage.setItem(probeKey, '1'.repeat(Math.max(1, probeBytes)));
    localStorage.removeItem(probeKey);
    return null;
  } catch (err) {
    return isQuotaExceededError(err) ? 'quota' : 'security';
  }
}

// QuotaExceededError across engines: the standard name, legacy WebKit/Blink
// code 22, and Firefox's NS_ERROR_DOM_QUOTA_REACHED (code 1014).
export function isQuotaExceededError(err) {
  if (!err) return false;
  return err.name === 'QuotaExceededError'
    || err.name === 'NS_ERROR_DOM_QUOTA_REACHED'
    || err.code === 22
    || err.code === 1014;
}

// #2673 — the entry point (audience or entry scenario, see
// lib/landingAudience.js's getGuestEntryKey) a stored guest token was minted
// through. AuthContext.loginAsGuest compares this against the CURRENT entry
// key before deciding whether a stored guest token may be reused instead of
// minting a fresh one. Wrapped in try/catch like every other localStorage
// access here: private-mode Safari throws, and a storage failure must fail
// toward "always mint" (the pre-#2673 behaviour), never toward a crash.
const GUEST_ENTRY_KEY_STORAGE_KEY = 'chatbot_guest_entry_key';

export function getStoredGuestEntryKey() {
  try {
    return localStorage.getItem(GUEST_ENTRY_KEY_STORAGE_KEY);
  } catch {
    return null;
  }
}

// Pass `null` to clear (used when a stored token turns out to be
// invalid/revoked) — no separate clear function; one call shape for both
// directions.
export function storeGuestEntryKey(entryKey) {
  try {
    if (entryKey) localStorage.setItem(GUEST_ENTRY_KEY_STORAGE_KEY, entryKey);
    else localStorage.removeItem(GUEST_ENTRY_KEY_STORAGE_KEY);
  } catch {
    // Best-effort — a failed write just means the next landing always mints.
  }
}

// Decode a JWT payload (base64url) without verifying the signature. Returns
// null for any malformed token. Callers decide their own fail-direction.
export function decodeTokenPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    let payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = payload.length % 4;
    if (pad) payload += '='.repeat(4 - pad);
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

// Decode a JWT payload and check if it's expired (30s buffer to avoid mid-flight failures).
// Returns true (treat as expired) for any parse failure or missing exp claim.
export function isTokenExpired(token) {
  const decoded = decodeTokenPayload(token);
  if (!decoded || typeof decoded.exp !== 'number') return true;
  return decoded.exp < Math.floor(Date.now() / 1000) + 30;
}

// #2540: expose the raw `exp` claim (epoch seconds) so a caller reporting an
// expired-token failure can name WHEN it expired, not just THAT it did.
// Returns null for any parse failure or missing exp claim — same
// fail-direction as isTokenExpired's decode step.
export function getTokenExpiry(token) {
  const decoded = decodeTokenPayload(token);
  return decoded && typeof decoded.exp === 'number' ? decoded.exp : null;
}

/**
 * Issue #1875: is the current session a demo guest?
 *
 * Guest JWTs carry `is_guest: true` (AuthService.generateGuestJWT) and are
 * restricted server-side to GUEST_ALLOWED_ROUTES — every other route 403s with
 * DEMO_SCOPE_RESTRICTED. Because guests are issued `role: 'learner'`, the
 * learner-gated frontend surfaces would otherwise fire fetches that can only
 * ever 403 (~1,000/week in prod, and the /auth/onboarding one floods Sentry).
 * Callers use this to skip those requests up front.
 *
 * Fails OPEN (returns false) on a missing or malformed token: "not a guest" is
 * the status-quo path, so a decode failure can never silently disable a real
 * learner's features — it just restores the pre-#1875 behaviour.
 *
 * Prefer `user.is_guest` from AuthContext inside React components; this exists
 * for plain modules (hooks, lib/) that have no access to context.
 */
export function isGuestSession() {
  // The localStorage read is wrapped because this helper runs from plain
  // modules on the voice connect path, where `localStorage` may be absent
  // (non-browser context) or throw SecurityError (sandboxed iframe, some
  // privacy modes). Throwing here would break the caller outright — strictly
  // worse than the 403 this replaces — so any failure takes the fail-open path.
  let token;
  try {
    token = getStoredAuthToken();
  } catch {
    return false;
  }
  if (!token) return false;
  return decodeTokenPayload(token)?.is_guest === true;
}

/**
 * Auth-aware fetch wrapper that handles 401 / 403-INVALID_TOKEN responses globally.
 * On detection, clears the stored token and dispatches a `sessionExpired` event
 * so AuthContext can log the user out. Returns a synthetic 401 Response when the
 * stored JWT is already expired client-side, avoiding a wasted network call.
 *
 * @param {string | URL | Request} url
 * @param {RequestInit} [options={}]
 * @returns {Promise<Response>}
 */
export async function authFetch(url, options = {}) {
  const token = getStoredAuthToken();

  if (token && isTokenExpired(token)) {
    if (!sessionExpiredTriggered) {
      sessionExpiredTriggered = true;
      clearStoredAuthToken();
      window.dispatchEvent(new CustomEvent('sessionExpired', {
        detail: { message: 'Your session has expired. Please log in again.' }
      }));
    }
    return new Response(JSON.stringify({ error: 'Token expired' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  const headers = {
    ...options.headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  // eslint-disable-next-line no-restricted-syntax -- authFetch implementation
  const response = await fetch(url, { ...options, headers });

  // Issue #1400: backend authenticateToken now returns 401 (not 403) for an
  // expired/invalid JWT (code INVALID_TOKEN), so a plain 401 is the primary
  // signal. The 403+INVALID_TOKEN branch below is retained ONLY as a
  // transitional fallback for any stale Cloud Run revision still serving the
  // old 403 during a rollout window; it can be deleted once prod has fully
  // cut over.
  let isAuthError = response.status === 401;

  if (response.status === 403 && token) {
    try {
      const clonedResponse = response.clone();
      const data = await clonedResponse.json();
      isAuthError = data.code === 'INVALID_TOKEN';
    } catch {
      isAuthError = false;
    }
  }

  // #3406 / mega115 plan check finding 2: a 401 is a verdict on the token
  // THIS request carried, not on whatever storage holds when the response
  // lands. If another tab (or a login in this tab) replaced the token while
  // the request was in flight, clearing storage here would sign out a
  // session nobody rejected. Only the token that was actually refused may
  // be cleared, and only then is the session-expired event raised.
  if (isAuthError && !sessionExpiredTriggered && getStoredAuthToken() === token) {
    sessionExpiredTriggered = true;
    clearStoredAuthToken();
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { message: 'Your session has expired. Please log in again.' }
    }));
  }

  return response;
}

// Re-export API_BASE so callers that used to get it via services/auth keep working.
export { API_BASE };
